import { cn } from "../utils/cn";

// ── Icons ─────────────────────────────────────────────────────────────────────

function TrendUpIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M22 7l-8.5 8.5-5-5L2 17" />
      <path d="M16 7h6v6" />
    </svg>
  );
}

function TrendDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M22 17l-8.5-8.5-5 5L2 7" />
      <path d="M16 17h6v-6" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type StatCardIconVariant = "purple" | "teal" | "orange" | "amber";

export interface StatCardProps {
  label: string;
  value: string | number;
  prefix?: string;
  suffix?: string;
  trend?: number;
  trendLabel?: string;
  icon?: React.ReactNode;
  iconVariant?: StatCardIconVariant;
  /** Legacy: custom className on icon wrapper — use iconVariant instead */
  iconColor?: string;
  isLoading?: boolean;
  className?: string;
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function StatCardSkeleton() {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 space-y-2.5">
        <div className="h-3.5 w-20 rounded-md skeleton-shimmer" />
        <div className="h-8 w-28 rounded-md skeleton-shimmer" />
        <div className="h-3.5 w-32 rounded-md skeleton-shimmer" />
      </div>
      <div className="w-11 h-11 rounded-xl skeleton-shimmer shrink-0" />
    </div>
  );
}

// ── Icon variant map ──────────────────────────────────────────────────────────

const ICON_VARIANT: Record<StatCardIconVariant, string> = {
  purple: "icon-bg-purple",
  teal:   "icon-bg-teal",
  orange: "icon-bg-orange",
  amber:  "icon-bg-amber",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function StatCard({
  label,
  value,
  prefix,
  suffix,
  trend,
  trendLabel,
  icon,
  iconVariant,
  iconColor,
  isLoading = false,
  className,
}: StatCardProps) {
  const isPositive = trend !== undefined && trend >= 0;

  const iconWrapperClass = iconVariant
    ? cn("shrink-0 w-11 h-11 rounded-xl flex items-center justify-center", ICON_VARIANT[iconVariant])
    : cn("shrink-0 w-11 h-11 rounded-xl flex items-center justify-center", iconColor ?? "icon-bg-purple");

  return (
    <div
      className={cn(
        "rounded-xl p-5",
        "bg-white dark:bg-surface-raised",
        "border border-border-default",
        "transition-all duration-200",
        "hover:border-border-strong",
        className,
      )}
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {isLoading ? (
        <StatCardSkeleton />
      ) : (
        <div className="flex items-start justify-between gap-4">
          {/* Text section */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-content-secondary mb-3 truncate">
              {label}
            </p>

            {/* Value */}
            <div className="flex items-baseline gap-0.5">
              {prefix && (
                <span className="text-base font-medium text-content-secondary">{prefix}</span>
              )}
              <span className="text-[28px] font-bold leading-none font-display text-content-primary">
                {value}
              </span>
              {suffix && (
                <span className="text-base font-medium text-content-secondary">{suffix}</span>
              )}
            </div>

            {/* Trend */}
            {trend !== undefined && (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <span
                  className={cn(
                    "inline-flex items-center gap-0.5 text-xs font-semibold",
                    isPositive
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-500 dark:text-red-400",
                  )}
                >
                  {isPositive ? <TrendUpIcon /> : <TrendDownIcon />}
                  {isPositive ? "+" : ""}
                  {trend}%
                </span>
                {trendLabel && (
                  <span className="text-xs text-content-tertiary">{trendLabel}</span>
                )}
              </div>
            )}
          </div>

          {/* Icon */}
          {icon && (
            <div className={iconWrapperClass}>
              {icon}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

StatCard.displayName = "StatCard";
